import { FilterValue } from 'antd/es/table/interface';
import { TCreateConverter, TFetchConverter } from 'data/types/converter.types';
import {
  dateConverter,
  dateShortenFormat,
  dateTimeFormatDB,
  sortAgeGroups,
  convertStateToUpperCase,
} from 'helpers/utils';
import {
  EEventFilterParams,
  EEventSortParams,
  EVENT_NO_ELC_FILTER_VALUE,
  TAddEventPhotoDB,
  TAddEventPhotoUI,
  TCapacityDB,
  TCapacityUI,
  TClassesDataDB,
  TClassesDataUI,
  TClassItemDB,
  TClassItemUI,
  TDeleteEventClassDB,
  TDeleteEventClassUI,
  TDuplicateEventDB,
  TDuplicateEventUI,
  TPhotoGalleryDB,
  TPhotoGalleryUI,
  TFlyersDataUI,
  TSessionsDB,
  TSessionsUI,
  TClassCapacityEditDB,
  TClassCapacityEditUI,
  TEventChildcareProvidedEditDB,
  TEventChildcareProvidedEditUI,
  TEventClassAgeGroupEditDB,
  TEventClassAgeGroupEditUI,
  TEventClassDataDB,
  TEventClassDataUI,
  TEventClassDateTimeEditDB,
  TEventClassDateTimeEditUI,
  TEventClassFacilitatorEditDB,
  TEventClassFacilitatorEditUI,
  TEventClassLanguageEditDB,
  TEventClassLanguageEditUI,
  TEventDataDB,
  TEventDataUI,
  TEventDescriptionEditDB,
  TEventDescriptionEditUI,
  TEventTitleEditDB,
  TEventTitleEditUI,
  TEventTypeEditDB,
  TEventTypeEditUI,
  TEventDetailsDataDB,
  TEventDetailsDataUI,
  TEventFoodProvidedEditDB,
  TEventFoodProvidedEditUI,
  TEventCreateDB,
  TEventCreateUI,
  TEventGalleryDB,
  TEventGalleryUI,
  TEventHostDataDB,
  TEventHostDataUI,
  TEventHostEditDB,
  TEventHostEditUI,
  TEventLocationDB,
  TEventLocationEditDB,
  TEventLocationEditUI,
  TEventLocationUI,
  TEventSessionDataDB,
  TEventSessionDataUI,
  TEventSessionItemDB,
  TEventSessionItemUI,
  TEventShippingAddressEditDB,
  TEventShippingAddressEditUI,
  TEventLocationAddressEditUI,
  TEventLocationAddressEditDB,
  TEventShipmentLocationEditUI,
  TEventShipmentLocationEditDB,
  TEventSiteCoordinatorDB,
  TEventSiteCoordinatorEditDB,
  TEventSiteCoordinatorEditUI,
  TEventSiteCoordinatorUI,
  TEventCoordinatorEditDB,
  TEventCoordinatorEditUI,
  TEventCapacityEditUI,
  TEventCapacityEditDB,
  TEventMeetingLinkEditUI,
  TEventMeetingLinkEditDB,
  EEventCategory,
  EEventPartType,
  ECapacityEmptyTexts,
  TEventChildCareUI,
  TEventChildCareDB,
  EAttendanceType,
  EEventStatus,
  EEventDetailsAllowedActions,
  EEventStandaloneType,
  EEventSeriesType,
  TEventSessionDateDB,
  TEventSessionDateUI,
  TPartsUI,
  TPartsDB,
  TEventPartDateTimeEditDB,
  TEventPartDateTimeEditUI,
  TEventsNoteUI,
  TEventsNoteDB,
  TEventPartKitOrdersDB,
  TEventPartKitOrdersUI,
  TEventPartTimeEditUI,
  TEventPartTimeEditDB,
  TAddEventRegistrationUI,
  TAddEventRegistrationDB,
  TExportEventsRequest,
} from 'data/types/events.types';
import {
  EAgeGroup,
  TSortParams,
  TTableFilters,
} from 'data/types/generic.types';
import {
  addressConvertor,
  addressShortDataConverter,
  allowOperationsAgeGroupValueConverter,
  allowOperationsConverter,
  allowOperationsLanguageValueConverter,
  allowOperationsShortAddressWithConverter,
  allowOperationsWithBooleanValueConverter,
  allowOperationsWithDateTimeValueConverter,
  allowOperationsWithStringValueConverter,
  minimalRegistrationContactToDb,
  phoneNumberConverter,
} from './general.converters';
import { tagsGetConverter } from './tags.convertor';

export const eventSessionItemConverter: TFetchConverter<
  TEventSessionItemUI,
  TEventSessionItemDB
> = {
  fromDb: data => ({
    ageGroup: data.ageGroup,
    language: data.language,
    count: data.count,
  }),
};

export const eventHostConverter: TFetchConverter<
  TEventHostDataUI,
  TEventHostDataDB
> = {
  fromDb: data => ({
    id: data.id,
    name: data.name,
    address: addressShortDataConverter.fromDb(data.address),
    allowedOperations: allowOperationsConverter.fromDb(data.allowedOperations),
    ...(data.hostType && { hostType: data.hostType }),
  }),
};

export const eventLocationConverter: TFetchConverter<
  TEventLocationUI,
  TEventLocationDB
> = {
  fromDb: data => ({
    id: data.id,
    name: data.name,
    address: addressShortDataConverter.fromDb(data.address),
    nameAndAddress: `${data.name || ''} ${
      data.name && data.address?.formattedAddress ? '|' : ''
    } ${data.address?.formattedAddress || ''}`.trim(),
    allowedOperations: allowOperationsConverter.fromDb(data.allowedOperations),
  }),
};

export const eventSiteCoordinatorConverter: TFetchConverter<
  TEventSiteCoordinatorUI,
  TEventSiteCoordinatorDB
> = {
  fromDb: data => ({
    id: data.id,
    allowedOperations: allowOperationsConverter.fromDb(data.allowedOperations),
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    phone: (data.phone && phoneNumberConverter.fromDb(data.phone)) || '',
    profilePicture: data.profilePicture,
    ...((data?.firstName || data?.lastName) && {
      fullName: `${data?.firstName || ''} ${data?.lastName || ''}`.trim(),
    }),
  }),
};

export const eventCreateConverter: TCreateConverter<
  TEventCreateUI,
  TEventCreateDB
> = {
  toDb: data => ({
    type: data.type,
    title: data.title,
    attendanceType: data.attendanceType,
    ...(data.hostType === 'ELC' ? { elcId: data.host } : { hostId: data.host }),
    numberOfDays: data.numberOfDays,
    hostedPrivately: data.hostedPrivately,
    requiresRegistration: data.requiresRegistration,
  }),
};

export const eventClassSessionConverter: TCreateConverter<
  TEventSessionDataUI,
  TEventSessionDataDB
> = {
  toDb: data => ({
    partType: data.partType,
    ...(data.date && { dateTime: dateConverter(data.date, dateTimeFormatDB) }),
    facilitatorId: data.facilitator,
  }),
};

export const eventClassCreateConverter: TCreateConverter<
  TEventClassDataUI,
  TEventClassDataDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    ageGroup: data.age,
    language: data.language,
    ...(data.capacity && { capacity: data.capacity }),
    sessions: data.sessions.map(session =>
      eventClassSessionConverter.toDb(session),
    ),
  }),
};

export const duplicateConverter: TCreateConverter<
  TDuplicateEventUI,
  TDuplicateEventDB
> = {
  toDb: data => ({
    eventId: data.eventId,
  }),
};

export const deleteEventClassConverter: TCreateConverter<
  TDeleteEventClassUI,
  TDeleteEventClassDB
> = {
  toDb: data => ({
    classId: data.classId,
    eventId: data.eventId,
  }),
};

export const eventSessionDateConverter: TFetchConverter<
  TEventSessionDateUI[],
  TEventSessionDateDB
> = {
  fromDb: data => {
    return Object.entries(data).map(([key, value]) => {
      return {
        date: value && dateConverter(value, dateShortenFormat),
        partType: key as EEventPartType,
      };
    });
  },
};

export const eventGetConverter: TFetchConverter<TEventDataUI, TEventDataDB> = {
  fromDb: data => ({
    eventId: data.eventId,
    hostName: data.hostName,
    hostId: data.hostId,
    attendanceType: data.attendanceType,
    createdDate:
      data.createdDate && dateConverter(data.createdDate, dateShortenFormat),
    city: data.city,
    englishSessions: (data.englishSessions || []).map(session =>
      eventSessionItemConverter.fromDb(session),
    ),
    spanishSessions: (data.spanishSessions || []).map(session =>
      eventSessionItemConverter.fromDb(session),
    ),
    hostedPrivately: data.hostedPrivately,
    eventCategory: data.eventCategory,
    eventStatus: data.eventStatus,
    eventType: data.eventType,
    tags: data.tags && tagsGetConverter.fromDb(data.tags),
    locationId: data.locationId,
    locationName: data.locationName,
    registrantCount: data.registrantCount,
    attendanceCount: data.attendanceCount,
    hasWaitList: data.hasWaitList,
    capacity: data.capacity ?? ECapacityEmptyTexts.UNLIMITED_SHORT,
    sessionDates: eventSessionDateConverter.fromDb(data.sessionDates),
    nextSessionPart: data.nextSessionPart ?? null,
    siteCoordinators: data.siteCoordinators,
    eventCoordinators: data.eventCoordinators,
    title: data.title,
    requiresRegistration: data.requiresRegistration,
    elcs: data.elcs ?? [],
  }),
};

export const sessionItemConverter: TFetchConverter<TSessionsUI, TSessionsDB> = {
  fromDb: data => {
    return {
      facilitator:
        data.facilitator &&
        eventSiteCoordinatorConverter.fromDb(data.facilitator),
      id: data.id,
      partType: data.partType,
    };
  },
};

export const eventCapacityConverter: TFetchConverter<TCapacityUI, TCapacityDB> =
  {
    fromDb: data => {
      return {
        allowedOperations: allowOperationsConverter.fromDb(
          data.allowedOperations,
        ),
        value: data?.value !== undefined ? String(data?.value) : '',
        showValue: data?.value ?? ECapacityEmptyTexts.UNLIMITED_LONG,
      };
    },
  };

export const classCapacityConverterConverter = (
  eventCapacity: TEventDetailsDataDB['capacity']['value'],
): TFetchConverter<TCapacityUI, TCapacityDB> => {
  return {
    fromDb: data => {
      const emptyText = eventCapacity
        ? ECapacityEmptyTexts.LIMITED_WITH_EVENT
        : ECapacityEmptyTexts.UNLIMITED_LONG;
      return {
        allowedOperations: allowOperationsConverter.fromDb(
          data.allowedOperations,
        ),
        value: data?.value !== undefined ? String(data?.value) : '',
        showValue: data.value ?? emptyText,
      };
    },
  };
};

export const classItemConverter = (
  eventCapacity: TEventDetailsDataDB['capacity']['value'],
): TFetchConverter<TClassItemUI, TClassItemDB> => {
  return {
    fromDb: data => {
      return {
        ageGroup: allowOperationsAgeGroupValueConverter.fromDb(data.ageGroup),
        capacity: classCapacityConverterConverter(eventCapacity).fromDb(
          data.capacity,
        ),
        id: data.id,
        language: allowOperationsLanguageValueConverter.fromDb(data.language),
        sessions: data.sessions.map(item => sessionItemConverter.fromDb(item)),
        registrantsCount: data.registrantsCount,
      };
    },
  };
};

export const classesConverter = (
  eventCapacity: TEventDetailsDataDB['capacity']['value'],
): TFetchConverter<TClassesDataUI, TClassesDataDB> => {
  return {
    fromDb: data => {
      return {
        value: data.value?.map(item =>
          classItemConverter(eventCapacity).fromDb(item),
        ),
        allowedOperations: allowOperationsConverter.fromDb(
          data.allowedOperations,
        ),
      };
    },
  };
};

export const uniqueAgeGroupsConverter: TFetchConverter<
  EAgeGroup[],
  TClassesDataDB
> = {
  fromDb: classes => {
    const uniqueAgeGroups = Array.from(
      new Set(classes.value?.map(item => item.ageGroup.value)),
    );

    return sortAgeGroups(uniqueAgeGroups);
  },
};

export const partsConverter: TFetchConverter<TPartsUI, TPartsDB> = {
  fromDb: data => ({
    partType: data.partType,
    isPassed: data.isPassed,
    eventPartId: data.eventPartId,
    ...(data.date && {
      date: allowOperationsWithDateTimeValueConverter.fromDb(data.date),
    }),
    startTime: data.startTime,
    endTime: data.endTime,
  }),
};

export const flyersDataConverter: TFetchConverter<
  TFlyersDataUI,
  TEventDetailsDataDB
> = {
  fromDb: data => {
    const finalResult: TFlyersDataUI = {
      existingAgeGroups: uniqueAgeGroupsConverter.fromDb(data.classes),
      parts: (data.parts || []).map(part => partsConverter.fromDb(part)),
    };

    return finalResult;
  },
};

export const allowedActionsConverter = (
  status: TEventDetailsDataDB['status'],
  attendanceType: TEventDetailsDataDB['attendanceType'],
  requiresRegistration?: TEventDetailsDataDB['requiresRegistration'],
): TFetchConverter<
  TEventDetailsDataUI['allowedActions'],
  TEventDetailsDataDB['allowedActions']
> => {
  return {
    fromDb: data => {
      const actionsOrder = [
        EEventDetailsAllowedActions.PUBLISH,
        EEventDetailsAllowedActions.CANCEL,
        EEventDetailsAllowedActions.CLOSE,
        EEventDetailsAllowedActions.DELETE,
        EEventDetailsAllowedActions.DUPLICATE,
      ];

      const sortedActions: TEventDetailsDataUI['allowedActions'] = (
        data || []
      ).sort((a, b) => actionsOrder.indexOf(a) - actionsOrder.indexOf(b));

      if (status === EEventStatus.PUBLISHED && requiresRegistration) {
        sortedActions.push(
          EEventDetailsAllowedActions.DOWNLOAD_LANDING_QR,
          EEventDetailsAllowedActions.COPY_URL,
        );

        if (attendanceType === EAttendanceType.IN_PERSON) {
          // Add additional actions for in-person events
        }
      }

      return sortedActions;
    },
  };
};

export const eventDetailsConverter: TFetchConverter<
  TEventDetailsDataUI,
  TEventDetailsDataDB
> = {
  fromDb: data => ({
    id: data.id,
    isOwner: data.isOwner,
    childcareProvided:
      data.childcareProvided &&
      allowOperationsWithBooleanValueConverter.fromDb(data.childcareProvided),
    foodProvided:
      data.childcareProvided &&
      allowOperationsWithBooleanValueConverter.fromDb(data.foodProvided),
    host: data.host && eventHostConverter.fromDb(data.host),
    hostedPrivately: data.hostedPrivately,
    location: data.location && eventLocationConverter.fromDb(data.location),
    photo: allowOperationsWithStringValueConverter.fromDb(data.photo),
    shipmentAddress:
      data.shipmentAddress &&
      allowOperationsShortAddressWithConverter.fromDb(data.shipmentAddress),
    shipmentLocation:
      data.shipmentLocation &&
      eventLocationConverter.fromDb(data.shipmentLocation),
    siteCoordinators:
      data.siteCoordinators.value &&
      data.siteCoordinators.value.map(coordinator =>
        eventSiteCoordinatorConverter.fromDb(coordinator),
      ),
    eventCoordinators:
      data.eventCoordinators?.value?.map(coordinator =>
        eventSiteCoordinatorConverter.fromDb(coordinator),
      ) || [],
    status: data.status,
    type: data.type,
    shipmentAddressSameAsLocationAddress:
      data.shipmentAddressSameAsLocationAddress,
    locationSameAsHost: data.locationSameAsHost,
    typeCategory: data.typeCategory,
    classes: classesConverter(data.capacity.value).fromDb(data.classes),
    possibleAgeGroups: data.possibleAgeGroups,
    flyerData: flyersDataConverter.fromDb(data),
    description:
      data.description &&
      allowOperationsWithStringValueConverter.fromDb(data.description),
    capacity: eventCapacityConverter.fromDb(data.capacity),
    attendanceType: data.attendanceType,
    meetingLink:
      data.meetingLink &&
      allowOperationsWithStringValueConverter.fromDb(data.meetingLink),
    customQuestion:
      data.customQuestion &&
      allowOperationsWithStringValueConverter.fromDb(data.customQuestion),
    allowedActions: allowedActionsConverter(
      data.status,
      data.attendanceType,
      data.requiresRegistration,
    ).fromDb(data.allowedActions),
    isCCP: [
      EEventStandaloneType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_1,
      EEventStandaloneType.CHILD_CARE_AND_PRESCHOOL_PROGRAMS_PART_2,
      EEventSeriesType.LEARNING_CHILD_CARE_AND_PRESCHOOL_PROGRAMS,
    ].includes(data.type),
    actualSessionDates: data.actualSessionDates,
    hasStartedPart: data.hasStartedPart,
    parts: (data.parts || []).map(part => partsConverter.fromDb(part)),
    timezone: data.timezone,
    language: data.language,
    attendees: data.attendees,
    requiresRegistration: data.requiresRegistration,
    title: data.title,
    allowRegistration: data.allowRegistration ?? false,
  }),
};

export const eventChildCareConverter: TFetchConverter<
  TEventChildCareUI,
  TEventChildCareDB
> = {
  fromDb: data => ({
    ageGroup: data.ageGroup,
    count: data.count,
  }),
};

export const photoGalleryConverter: TFetchConverter<
  TPhotoGalleryUI,
  TPhotoGalleryDB
> = {
  fromDb: data => ({
    originalPhoto: data.originalPhoto,
    thumbnailPhoto: data.thumbnailPhoto,
  }),
};

export const eventGalleryConverter: TFetchConverter<
  TEventGalleryUI,
  TEventGalleryDB
> = {
  fromDb: data => ({
    photos:
      data.photos &&
      data.photos.map(item => photoGalleryConverter.fromDb(item)),
  }),
};

export const addEventPhotoConverter: TCreateConverter<
  TAddEventPhotoUI,
  TAddEventPhotoDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    photoUrl: data.photoUrl,
  }),
};

export const eventFilterConverter = (
  filters: TTableFilters<EEventFilterParams>['filters'],
) => {
  // ELC filter mixes real UUIDs with a sentinel "Not in an ELC" option;
  // split them so the server gets elcIds + includeNoElc per its contract.
  const rawElcValues = (filters?.elcIds ?? []) as string[];
  const includeNoElc = rawElcValues.includes(EVENT_NO_ELC_FILTER_VALUE);
  const elcIds = rawElcValues.filter(v => v !== EVENT_NO_ELC_FILTER_VALUE);

  const filtersToDB = {
    ...(filters?.statuses && {
      [EEventFilterParams.STATUS]: filters?.statuses.map(status => status),
    }),
    ...(filters?.types && {
      [EEventFilterParams.TYPE]: filters?.types.map(type => type),
    }),
    ...(filters?.cities && {
      [EEventFilterParams.CITY]: filters?.cities,
    }),
    ...(filters?.hostIds && {
      [EEventFilterParams.HOST]: filters?.hostIds,
    }),
    ...(filters?.tagIds && {
      [EEventFilterParams.TAGS]: filters?.tagIds,
    }),
    ...(filters?.locationIds && {
      [EEventFilterParams.LOCATION]: filters?.locationIds,
    }),
    ...(filters?.hostingTypes && {
      [EEventFilterParams.HOSTING_TYPES]: filters?.hostingTypes,
    }),
    ...(filters?.attendanceTypes && {
      [EEventFilterParams.ATTENDANCE_TYPE]: filters?.attendanceTypes,
    }),
    ...(filters?.siteCoordinator && {
      [EEventFilterParams.SITE_COORDINATOR]: filters?.siteCoordinator,
    }),
    ...(filters?.title && {
      [EEventFilterParams.TITLE]: filters?.title,
    }),
    ...(elcIds.length > 0 && { [EEventFilterParams.ELC]: elcIds }),
    ...(includeNoElc && { includeNoElc: true }),
    // Both capacity flags live on the Participation column's filter dropdown
    // (key = 'capacity'). We fan them back out to two boolean query params
    // the BE understands (?nearCapacity=true, ?overCapacity=true).
    ...((
      filters?.[EEventFilterParams.CAPACITY] as string[] | undefined
    )?.includes(EEventFilterParams.NEAR_CAPACITY) && {
      [EEventFilterParams.NEAR_CAPACITY]: true,
    }),
    ...((
      filters?.[EEventFilterParams.CAPACITY] as string[] | undefined
    )?.includes(EEventFilterParams.OVER_CAPACITY) && {
      [EEventFilterParams.OVER_CAPACITY]: true,
    }),
  };
  return filtersToDB;
};

// Events table column key -> column id of POST /events/export (GROW-4162)
const EVENT_COLUMN_KEY_TO_EXPORT_ID: Record<string, string> = {
  [EEventSortParams.CREATION_DATE]: 'createdDate',
  [EEventFilterParams.HOST]: 'hostName',
  [EEventFilterParams.ELC]: 'elcName',
  [EEventFilterParams.LOCATION]: 'locationName',
  [EEventFilterParams.CITY]: 'city',
  [EEventFilterParams.TYPE]: 'eventType',
  [EEventFilterParams.TITLE]: 'title',
  [EEventFilterParams.SITE_COORDINATOR]: 'eventCoordinators',
  [EEventFilterParams.CAPACITY]: 'participation',
  [EEventSortParams.NEXT_SESSION_DATE]: 'sessionDates',
  [EEventFilterParams.STATUS]: 'eventStatus',
  [EEventFilterParams.ATTENDANCE_TYPE]: 'attendanceType',
  [EEventFilterParams.TAGS]: 'tags',
};

// The table's search filters keep their text as a one-item array, while the
// export body takes a plain string.
const searchFilterText = (value: unknown) => {
  const text = [value].flat()[0];
  return text ? String(text).trim() : undefined;
};

export const buildEventsExportBody = ({
  filters,
  sortColumn,
  visibleColumnKeys,
  myEvents,
  eventIds,
}: {
  filters?: Record<string, FilterValue | null>;
  sortColumn?: TSortParams;
  visibleColumnKeys?: string[];
  myEvents?: boolean;
  eventIds?: string[];
}): TExportEventsRequest => {
  const columns = visibleColumnKeys
    ?.map(key => EVENT_COLUMN_KEY_TO_EXPORT_ID[key])
    .filter(Boolean);
  // Same filters as the list request, so the CSV matches the table.
  const { siteCoordinator, title, ...listFilters } = filters
    ? eventFilterConverter(filters)
    : {};
  const siteCoordinatorText = searchFilterText(siteCoordinator);
  const titleText = searchFilterText(title);

  return {
    ...(columns?.length && { columns }),
    filters: {
      ...listFilters,
      ...(siteCoordinatorText && { siteCoordinator: siteCoordinatorText }),
      ...(titleText && { title: titleText }),
      ...(myEvents && { myEvents }),
      ...(eventIds?.length && { eventIds }),
    } as TExportEventsRequest['filters'],
    // No sort sent means the backend default (next session date ascending),
    // which is also the list's default order.
    ...(sortColumn?.sortField &&
      sortColumn?.sortDirection && {
        sort: {
          field: sortColumn.sortField,
          direction: sortColumn.sortDirection,
        },
      }),
  };
};

export const eventClassAgeGroupEditConverter: TCreateConverter<
  TEventClassAgeGroupEditUI,
  TEventClassAgeGroupEditDB
> = {
  toDb: data => ({
    eventClassId: data.id,
    eventId: data.eventId,
    ageGroup: data.value,
  }),
};

export const eventClassLanguageEditConverter: TCreateConverter<
  TEventClassLanguageEditUI,
  TEventClassLanguageEditDB
> = {
  toDb: data => ({
    eventClassId: data.id,
    eventId: data.eventId,
    language: data.value,
  }),
};

export const eventClassSessionDateTimeEditConverter: TCreateConverter<
  TEventClassDateTimeEditUI,
  TEventClassDateTimeEditDB
> = {
  toDb: data => ({
    eventClassId: data.id,
    eventSessionId: data.eventSessionId,
    eventId: data.eventId,
    date: data.value && dateConverter(data.value, dateTimeFormatDB),
  }),
};

export const eventPartDateTimeEditConverter: TCreateConverter<
  TEventPartDateTimeEditUI,
  TEventPartDateTimeEditDB
> = {
  toDb: data => {
    return {
      date: data.dateTime && dateConverter(data.dateTime, dateTimeFormatDB),
      eventId: data.eventId,
      eventPartId: data.eventPartId,
    };
  },
};

export const eventPartTimeEditConverter: TCreateConverter<
  TEventPartTimeEditUI,
  TEventPartTimeEditDB
> = {
  toDb: data => {
    return {
      time: data.time,
      timeType: data.timeType,
      eventId: data.eventId,
      eventPartId: data.eventPartId,
    };
  },
};

export const eventClassSessionFacilitatorEditConverter: TCreateConverter<
  TEventClassFacilitatorEditUI,
  TEventClassFacilitatorEditDB
> = {
  toDb: data => ({
    eventClassId: data.id,
    eventSessionId: data.eventSessionId,
    userId: data.value || null,
    eventId: data.eventId,
  }),
};

export const classCapacityEditConverter: TCreateConverter<
  TClassCapacityEditUI,
  TClassCapacityEditDB
> = {
  toDb: data => ({
    eventClassId: data.id,
    eventId: data.eventId,
    capacity: data.value,
  }),
};

export const eventCapacityEditConverter: TCreateConverter<
  TEventCapacityEditUI,
  TEventCapacityEditDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    capacity: data.value || null,
  }),
};

export const eventLocationEditConverter: TCreateConverter<
  TEventLocationEditUI,
  TEventLocationEditDB
> = {
  toDb: data => ({
    locationId: data.value,
    eventId: data.id,
  }),
};

export const eventHostEditConverter: TCreateConverter<
  TEventHostEditUI,
  TEventHostEditDB
> = {
  toDb: data => ({
    hostId: data.value || null,
    eventId: data.id,
  }),
};

export const eventMeetingLinkEditConverter: TCreateConverter<
  TEventMeetingLinkEditUI,
  TEventMeetingLinkEditDB
> = {
  toDb: data => ({
    meetingLink: data.value || null,
    eventId: data.id,
  }),
};

export const eventSiteCoordinatorEditConverter: TCreateConverter<
  TEventSiteCoordinatorEditUI,
  TEventSiteCoordinatorEditDB
> = {
  toDb: data => ({
    eventId: data.id,
    userIds: data.value,
  }),
};

export const eventCoordinatorEditConverter: TCreateConverter<
  TEventCoordinatorEditUI,
  TEventCoordinatorEditDB
> = {
  toDb: data => ({
    eventId: data.id,
    userIds: data.value,
  }),
};

export const eventFoodProvidedEditConverter: TCreateConverter<
  TEventFoodProvidedEditUI,
  TEventFoodProvidedEditDB
> = {
  toDb: data => ({
    foodProvided: data.value,
    eventId: data.id,
  }),
};

export const eventChildcareEditConverter: TCreateConverter<
  TEventChildcareProvidedEditUI,
  TEventChildcareProvidedEditDB
> = {
  toDb: data => ({
    childcareProvided: data.value,
    eventId: data.id,
  }),
};

export const eventShipingAddressConverter: TCreateConverter<
  TEventShippingAddressEditUI,
  TEventShippingAddressEditDB
> = {
  toDb: data => ({
    eventId: data.id,
    address: addressConvertor.toDb(data),
  }),
};

export const eventLocationAddressConverter: TCreateConverter<
  TEventLocationAddressEditUI,
  TEventLocationAddressEditDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    address: {
      placeId: data.address.placeId,
      formattedAddress: data.address.formattedAddress,
      country: data.address.country,
      state: data.address.state && convertStateToUpperCase(data.address.state),
      city: data.address.city,
      streetNumber: data.address.streetNumber,
      street: data.address.street,
      zipCode: data.address.zipCode,
      latitude: data.address.latitude,
      longitude: data.address.longitude,
    },
  }),
};

export const eventShipmentLocationEditConverter: TCreateConverter<
  TEventShipmentLocationEditUI,
  TEventShipmentLocationEditDB
> = {
  toDb: data => ({
    locationId: data.value,
    eventId: data.id,
  }),
};

export const eventDescriptionEditConverter: TCreateConverter<
  TEventDescriptionEditUI,
  TEventDescriptionEditDB
> = {
  toDb: data => ({
    description: data.value,
    eventId: data.id,
  }),
};

export const eventTitleEditConverter: TCreateConverter<
  TEventTitleEditUI,
  TEventTitleEditDB
> = {
  toDb: data => ({
    title: data.value,
    eventId: data.id,
  }),
};

export const eventTypeEditConverter: TCreateConverter<
  TEventTypeEditUI,
  TEventTypeEditDB
> = {
  toDb: data => ({
    type: data.value,
    eventId: data.id,
  }),
};

export const eventCategoryToCount: Record<EEventCategory, number> = {
  [EEventCategory.STANDALONE]: 1,
  [EEventCategory.SERIES_WITH_TWO_PARTS]: 2,
  [EEventCategory.SERIES_WITH_THREE_PARTS]: 3,
};

export const eventPartsToCount: Record<EEventPartType, number> = {
  [EEventPartType.STANDALONE]: 0,
  [EEventPartType.PART_ONE]: 1,
  [EEventPartType.PART_TWO]: 2,
  [EEventPartType.PART_THREE]: 3,
};

export const eventsNoteConverter: TFetchConverter<
  TEventsNoteUI,
  TEventsNoteDB
> = {
  fromDb: data => {
    return {
      eventId: data.eventId,
      noteId: data.noteId,
      note: data.note,
    };
  },
};

export const createEventsNoteConverter: TCreateConverter<
  TEventsNoteUI,
  TEventsNoteDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    noteId: data.noteId,
    note: data.note,
  }),
};

export const editEventsNoteConverter: TCreateConverter<
  TEventsNoteUI,
  TEventsNoteDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    noteId: data.noteId,
    note: data.note,
  }),
};

export const deleteEventsNoteConverter: TCreateConverter<
  TEventsNoteUI,
  TEventsNoteDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    noteId: data.noteId,
    note: data.note,
  }),
};

export const eventPartKitOrdersConverter = {
  fromDb: (data: TEventPartKitOrdersDB): TEventPartKitOrdersUI => {
    const items: Record<EEventPartType, any> = {} as Record<
      EEventPartType,
      any
    >;

    Object.keys(data.items).forEach(key => {
      const enumKey = key as EEventPartType;
      items[enumKey] = data.items[key];
    });

    return {
      eventId: data.eventId,
      items,
    };
  },
};

export const addEventRegistrationConverter: TCreateConverter<
  TAddEventRegistrationUI,
  TAddEventRegistrationDB
> = {
  toDb: data => ({
    eventId: data.eventId,
    ...minimalRegistrationContactToDb(data),
    allowedTakePicture: false,
    customQuestionAnswer: data.customQuestionAnswer?.trim() || undefined,
    childcareRequirements: data.childcareRequirements?.length
      ? data.childcareRequirements.map(r => ({
          ageGroup: r.ageGroup,
          childCount: r.childCount,
        }))
      : undefined,
  }),
};
